import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, IdCard, LockKeyhole, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Btn, ErrorNotice, Field, SelectField } from '../shared';
import { API, api, message } from '../../lib/api';
import { canShareFile, canvasBlob, downloadBlob, shareBlob } from '../../lib/shareCard';
import { drawPlayerCard, shortName } from '../../lib/playerCard';

const LOGO = '/brand/sports-hub-connect-wordmark.png';
const fileSlug = name => (name || 'player').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'player';

export const PlayerCardShare = ({ athleteId }) => {
  const qrRef = useRef(null);
  const [passport, setPassport] = useState(null);
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(30);
  const [metricKey, setMetricKey] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => api.get(`/player-passports/${athleteId}`)
    .then(r => setPassport(r.data)).catch(e => setError(message(e))), [athleteId]);
  useEffect(() => { load(); }, [load]);

  const metrics = (passport?.evaluations || []).flatMap(evaluation =>
    (evaluation.metrics || []).map((metric, index) => ({ ...metric, key: `${evaluation.id}:${index}`, date: evaluation.date })));
  const metric = metrics.find(m => m.key === metricKey) || null;

  const build = async () => {
    const attempt = async photoSrc => {
      const canvas = await drawPlayerCard({
        athlete: passport.athlete, metric, email: invitation?.email || email,
        qrCanvas: qrRef.current?.querySelector('canvas'), logoSrc: LOGO, photoSrc
      });
      return { src: canvas.toDataURL('image/png'), blob: await canvasBlob(canvas) };
    };
    const photoSrc = passport.athlete.photo_id ? `${API}/files/${passport.athlete.photo_id}` : null;
    try { return await attempt(photoSrc); }
    catch { return attempt(null); }
  };

  const create = async event => {
    event.preventDefault();
    setBusy(true); setError(''); setCard(null);
    try {
      const result = await api.post(`/player-passports/${athleteId}/invitations`, { email, expires_in_days: days });
      setInvitation({ token: result.data.token, email: result.data.email });
    } catch (e) { setError(message(e)); setBusy(false); }
  };

  useEffect(() => {
    if (!invitation || !passport) return;
    let active = true;
    build()
      .then(result => { if (active) { setCard(result); setBusy(false); toast.success('Player card ready'); } })
      .catch(() => { if (active) { setError('The player card could not be generated. Try again.'); setBusy(false); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, passport, metricKey]);

  const slug = fileSlug(shortName(passport?.athlete?.name));
  const filename = `${slug}-sports-hub-connect.png`;
  const share = async () => {
    try {
      if (await shareBlob(card.blob, filename, `${shortName(passport.athlete.name)} · Sports Hub Connect`)) return;
      downloadBlob(card.blob, filename);
      toast.success('Saved to your device — sharing isn’t available in this browser');
    } catch { /* the share sheet was dismissed */ }
  };

  if (!passport) return <section className="player-card-share" data-testid="player-card-share"><p className="share-section-label">SHARE THEIR PLAYER CARD</p><ErrorNotice text={error} /></section>;

  return <section className="player-card-share" data-testid="player-card-share">
    <p className="share-section-label" data-testid="player-card-share-label">SHARE THEIR PLAYER CARD</p>
    <p className="form-hint" data-testid="player-card-share-intro">A square image with their name, age, position and a private Passport invitation QR — the code only opens for the email you choose.</p>
    <form className="form-stack" onSubmit={create}>
      <Field id="player-card-email" label="Invitation opens only for this email" type="email" placeholder="grandma@example.com"
        value={email} onChange={e => setEmail(e.target.value)} required disabled={busy} />
      <div className="form-grid">
        <SelectField id="player-card-expiry" label="Access expires in" value={days} onChange={e => setDays(Number(e.target.value))} disabled={busy}>
          {[7, 30, 90].map(n => <option key={n} value={n}>{n} days</option>)}
        </SelectField>
        <SelectField id="player-card-metric" label="Headline metric" value={metricKey} onChange={e => setMetricKey(e.target.value)} disabled={busy || !metrics.length}>
          <option value="">{metrics.length ? 'No metric' : 'No evaluations yet'}</option>
          {metrics.map(m => <option key={m.key} value={m.key}>{m.name} · {m.value}{m.unit ? ' ' + m.unit : ''}</option>)}
        </SelectField>
      </div>
      <ErrorNotice text={error} />
      <Btn secondary type="submit" data-testid="player-card-generate-button" disabled={busy}><IdCard />{busy ? 'Building card…' : card ? 'Create a new card' : 'Create invitation & card'}</Btn>
    </form>
    {card && <div className="share-card-preview" data-testid="player-card-preview">
      <img src={card.src} alt={`${shortName(passport.athlete.name)} player card`} data-testid="player-card-image" />
      <p className="pp-muted" data-testid="player-card-invitation-note"><LockKeyhole size={13} style={{ display: 'inline', marginRight: 5 }} />Private invitation for {invitation.email}. Revoke it any time from Share on their Player Passport.</p>
      <div className="share-card-actions">
        <Btn data-testid="player-card-download" onClick={() => downloadBlob(card.blob, filename)}><Download />Download</Btn>
        <Btn secondary data-testid="player-card-share-button" onClick={share}><Share2 />{canShareFile() ? 'Share' : 'Share / Save'}</Btn>
      </div>
    </div>}
    <div className="share-qr-source" ref={qrRef} aria-hidden="true">
      {invitation && <QRCodeCanvas value={`${window.location.origin}/player-passport/invitations/${invitation.token}`} size={624} level="M" bgColor="#0a0a0a" fgColor="#ffffff" marginSize={2} />}
    </div>
  </section>;
};
